import {
  Spotlight,
  type SpotlightActionData,
  type SpotlightActionGroupData,
} from '@mantine/spotlight'
import { useDebouncedValue } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import { IconDatabase, IconSearch } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { intelligenceApi } from '../../api/intelligence'
import { primaryNavigation } from './navigation'
import { isAllowlisted } from '../../api/allowlist'

type SpotlightActions = SpotlightActionData | SpotlightActionGroupData

const DATA_ASSETS_GROUP = 'Data assets'

/**
 * Filters only the static "Navigate" actions by the query (label/description
 * match, same as Spotlight's own default filter). The "Data assets" group is
 * left untouched because it already reflects a server-side search scoped to
 * the same query.
 */
function filterActions(query: string, actions: SpotlightActions[]): SpotlightActions[] {
  const term = query.trim().toLowerCase()
  if (!term) return actions

  const result: SpotlightActions[] = []
  for (const action of actions) {
    if (!('actions' in action) || action.group === DATA_ASSETS_GROUP) {
      result.push(action)
      continue
    }
    const matches = action.actions.filter(
      (item: SpotlightActionData) =>
        item.label?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
    )
    if (matches.length) result.push({ ...action, actions: matches })
  }
  return result
}

/**
 * App-wide command palette (⌘K / Ctrl+K), also opened from the Header search
 * field via `openGlobalSearch()`. Combines static navigation shortcuts with a
 * live, debounced search of Prism data assets via the Prism Intelligence API.
 */
export function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebouncedValue(query, 250)
  const trimmedQuery = debouncedQuery.trim()

  const allowed = isAllowlisted()
  const navActions: SpotlightActionData[] = useMemo(
    () =>
      primaryNavigation
        .filter((item) => allowed || !item.requiresDatabricks)
        .map(({ name, href, icon: Icon }) => ({
          id: `nav-${href}`,
          label: name,
          leftSection: <Icon size={18} />,
          onClick: () => navigate(href),
        })),
    [navigate, allowed]
  )

  const assetsSearch = useQuery({
    queryKey: ['intelligence', 'assets', 'search', trimmedQuery],
    queryFn: () => intelligenceApi.searchAssets(trimmedQuery),
    enabled: trimmedQuery.length >= 2,
  })

  const assetActions: SpotlightActionGroupData | null = useMemo(() => {
    if (trimmedQuery.length < 2) return null

    if (assetsSearch.isLoading) {
      return {
        group: DATA_ASSETS_GROUP,
        actions: [{ id: 'assets-loading', label: 'Searching data assets…', disabled: true }],
      }
    }
    if (assetsSearch.error) {
      return {
        group: DATA_ASSETS_GROUP,
        actions: [
          {
            id: 'assets-error',
            label: 'Asset search failed',
            description:
              assetsSearch.error instanceof Error
                ? assetsSearch.error.message
                : 'The connected service could not complete this request.',
            disabled: true,
          },
        ],
      }
    }
    if (!assetsSearch.data?.length) {
      return {
        group: DATA_ASSETS_GROUP,
        actions: [
          { id: 'assets-empty', label: 'No matching data assets', disabled: true },
        ],
      }
    }
    const MAX_RESULTS = 15
    const capped = assetsSearch.data.slice(0, MAX_RESULTS)
    const actions = capped.map((asset) => ({
      id: `asset-${asset.id}`,
      label: asset.name,
      description: [asset.type, asset.owner].filter(Boolean).join(' · '),
      leftSection: <IconDatabase size={18} />,
      onClick: () =>
        navigate(`/intelligence/lineage?asset=${encodeURIComponent(asset.id)}`),
    }))
    if (assetsSearch.data.length > MAX_RESULTS) {
      actions.push({
        id: 'assets-more',
        label: `${assetsSearch.data.length - MAX_RESULTS} more — refine your search`,
        description: '',
        leftSection: <IconSearch size={18} />,
        onClick: () =>
          navigate(`/intelligence/assets`),
      })
    }
    return { group: DATA_ASSETS_GROUP, actions }
  }, [assetsSearch.data, assetsSearch.error, assetsSearch.isLoading, trimmedQuery, navigate])

  const actions: SpotlightActions[] = useMemo(
    () => [
      { group: 'Navigate', actions: navActions },
      ...(assetActions ? [assetActions] : []),
    ],
    [navActions, assetActions]
  )

  return (
    <Spotlight
      actions={actions}
      query={query}
      onQueryChange={setQuery}
      filter={filterActions}
      searchProps={{
        leftSection: <IconSearch size={18} />,
        placeholder: 'Search Prism pages and data assets…',
      }}
      nothingFound="No pages or data assets matched your search."
      shortcut={['mod + K', 'mod + P']}
      returnFocus={false}
    />
  )
}
